'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ColorFamily {
  id: number;
  name: string;
  description: string;
  finishes: string[];
  applications: string[];
  icon: string;
  color: string;
}

const colorFamilies: ColorFamily[] = [
  {
    id: 1,
    name: 'Architectural Series',
    description: 'AAMA 2604/2605 compliant coatings for exterior building applications',
    finishes: ['Smooth', 'Textured', 'Metallic'],
    applications: ['Curtain walls', 'Window frames', 'Architectural panels'],
    icon: 'BuildingOfficeIcon',
    color: 'bg-blue-500'
  },
  {
    id: 2,
    name: 'Industrial Protection',
    description: 'Heavy-duty epoxy and polyester formulations for harsh environments',
    finishes: ['High-gloss', 'Semi-gloss', 'Matte'],
    applications: ['Equipment housings', 'Machinery guards', 'Structural steel'],
    icon: 'WrenchScrewdriverIcon',
    color: 'bg-gray-600'
  },
  {
    id: 3,
    name: 'Custom Colors',
    description: 'RAL, Pantone, and custom color matching with spectrophotometer verification',
    finishes: ['Any standard finish', 'Custom textures', 'Special effects'],
    applications: ['Branding requirements', 'Design specifications', 'Prototype matching'],
    icon: 'SwatchIcon',
    color: 'bg-purple-500'
  },
  {
    id: 4,
    name: 'Specialty Coatings',
    description: 'Functional coatings including anti-microbial, conductive, and high-temp',
    finishes: ['Functional', 'Performance-driven', 'Application-specific'],
    applications: ['Medical equipment', 'Electronics', 'High-heat applications'],
    icon: 'SparklesIcon',
    color: 'bg-amber-500'
  }
];

const ColorCapabilities = () => {
  const [selectedFamily, setSelectedFamily] = useState<number | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handleFamilyClick = (id: number) => {
    if (!isHydrated) return;
    setSelectedFamily(selectedFamily === id ? null : id);
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
            <Icon name="SwatchIcon" size={20} variant="outline" className="text-accent" />
            <span className="text-sm font-cta font-semibold text-accent">
              Unlimited Possibilities
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Color & Finish Capabilities
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From standard architectural colors to custom formulations, our coating library delivers the exact finish your project demands.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {colorFamilies.map((family) => (
            <div
              key={family.id}
              className="bg-card rounded-lg shadow-md hover:shadow-lg transition-all duration-base border border-border overflow-hidden"
            >
              <div className={`h-2 ${family.color}`} />
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 ${family.color} rounded-lg flex items-center justify-center`}>
                      <Icon name={family.icon as any} size={24} variant="solid" className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-headline font-bold text-foreground">
                        {family.name}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => handleFamilyClick(family.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label={`${isHydrated && selectedFamily === family.id ? 'Collapse' : 'Expand'} ${family.name} details`}
                  >
                    <Icon
                      name={isHydrated && selectedFamily === family.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={20}
                      variant="outline"
                      className="text-muted-foreground"
                    />
                  </button>
                </div>

                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {family.description}
                </p>

                {isHydrated && selectedFamily === family.id && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div>
                      <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                        Available Finishes
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {family.finishes.map((finish, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-cta font-semibold rounded-full"
                          >
                            {finish}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                        Common Applications
                      </div>
                      <div className="space-y-1">
                        {family.applications.map((app, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{app}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-8 text-center">
          <Icon name="BeakerIcon" size={48} variant="outline" className="text-primary-foreground mx-auto mb-4" />
          <h3 className="text-2xl font-headline font-bold text-primary-foreground mb-3">
            Custom Color Matching Service
          </h3>
          <p className="text-primary-foreground/90 max-w-2xl mx-auto mb-6">
            Our spectrophotometer-equipped lab can match any color sample with precision. Submit your reference for a custom formulation quote.
          </p>
          <button className="inline-flex items-center space-x-2 px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta font-bold hover:opacity-90 transition-all duration-base shadow-lg">
            <Icon name="DocumentTextIcon" size={20} variant="outline" />
            <span>Request Color Match</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ColorCapabilities;